import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'sv' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const defaultContext: LanguageContextType = {
  language: 'sv',
  setLanguage: () => {},
  t: (key: string) => key,
};

const LanguageContext = createContext<LanguageContextType>(defaultContext);

const translations = {
  sv: {
    // Navigation
    'nav.home': 'Hem',
    'nav.about': 'Om Oss',
    'nav.menu': 'Menyer',
    'nav.gallery': 'Galleri',
    'nav.contact': 'Kontakt',
    'nav.bookTable': 'Boka Bord',
    
    // Hero
    'hero.welcome': 'Välkommen till',
    'hero.tagline': 'Smaker som förgyller din dag',
    'hero.description': 'Upplev äkta smaker från Indien, Thailand och Japan i en lyxig och modern miljö. Sedan 2017 har vi levererat autentiska rätter som förenar kultur och kulinarisk excellens.',
    'hero.bookNow': 'Boka Bord Nu',
    'hero.seeMenus': 'Se Menyer',
    
    // About
    'about.title': 'Om Oss',
    'about.heading': 'En Smakupplevelse Utöver Det Vanliga',
    'about.description': 'Grundad 2017, Currylicious Yum Yum (tidigare känd som Curry Nam Nam), har som vision att föra äkta indiska, thailändska och japanska smaker vidare till våra gäster. Inspirerade av de rika kulinariska traditionerna i Indien, Thailand och Japan är vi dedikerade till att erbjuda en matupplevelse som speglar de livliga kulturerna och utsökta smakerna från dessa kök.',
    'about.freshIngredients': 'Färska råvaror',
    'about.freshDesc': 'Vi använder endast de färskaste och mest autentiska ingredienserna',
    'about.passion': 'Passion för mat',
    'about.passionDesc': 'Varje rätt tillagas med omsorg och kärlek av våra erfarna kockar',
    'about.service': 'Personlig service',
    'about.serviceDesc': 'Din upplevelse är vår prioritet, varje gång',
    'about.quality': 'Prisbelönt kvalitet',
    'about.qualityDesc': 'Erkänd för excellens i autentisk asiatisk matlagning',
    'about.timeline.1.label': 'Början',
    'about.timeline.1.title': 'En vision i Skanör',
    'about.timeline.1.desc': 'Drömmen om att förena indiska, thailändska och japanska smaker under ett och samma tak — i hjärtat av Skanör.',
    'about.timeline.2.label': 'Hantverket',
    'about.timeline.2.title': 'Tre kök, en passion',
    'about.timeline.2.desc': 'Erfarna kockar från tre kulinariska traditioner möts i köket med äkta kryddor och de mest färska råvarorna.',
    'about.timeline.3.label': 'Atmosfären',
    'about.timeline.3.title': 'En plats att minnas',
    'about.timeline.3.desc': 'Varm gästfrihet, omsorg om varje detalj och en miljö som välkomnar dig både inne och ute.',
    'about.timeline.4.label': 'Idag',
    'about.timeline.4.title': 'Resan fortsätter',
    'about.timeline.4.desc': 'Varje gäst, varje rätt, varje kväll — en del av den fortsatta historien om Currylicious Yum Yum.',
    'about.quote': '"Vår meny är en hyllning till autentiska ingredienser och hantverk, där traditionella recept möter moderna smakprofiler. Currylicious Yum Yum är en plats där gemenskap och kultur förenas, och vi välkomnar våra gäster att upptäcka de rika smakerna från Indien, Thailand och Japan i en varm och inbjudande miljö."',
    
    // Menu
    'menu.title': 'Våra Menyer',
    'menu.heading': 'Upptäck Våra Kulinariska Skapelser',
    'menu.description': 'Från klassiska curry från Indien till pad thai från Thailand och elegant sushi från Japan – vi erbjuder något för varje smak.',
    'menu.takeaway': 'Takeaway Meny',
    'menu.takeawayDesc': 'Ta med våra läckerheter hem. Perfekt för en mysig kväll.',
    'menu.seeTakeaway': 'Se Takeaway',
    'menu.bar': 'Bar Meny',
    'menu.barDesc': 'Exklusiva drycker och cocktails för en komplett upplevelse.',
    'menu.seeBar': 'Se Bar Meny',
    'menu.alacarte': 'À La Carte',
    'menu.alacarteDesc': 'Vår fullständiga dine-in meny med alla våra specialiteter.',
    'menu.seeAlacarte': 'Se À La Carte',
    'menu.allergies': 'Har du speciella allergier eller önskemål? Vi anpassar gärna våra rätter efter dina behov.',
    'menu.contact': 'Kontakta Oss för Anpassningar',
    
    // Video Gallery
    'video.title': 'Upplev',
    'video.heading': 'Se Vår Restaurang',
    'video.description': 'Ta en virtuell tur genom vår restaurang och upplev atmosfären.',
    'video.atmosphere': 'Restaurang Atmosfär',
    'video.dishes': 'Våra Rätter',
    'video.experience': 'Kulinarisk Upplevelse',
    
    // Gallery
    'gallery.title': 'Galleri',
    'gallery.heading': 'Visuella Smaker',
    'gallery.description': 'En titt på några av våra mest populära rätter och den eleganta atmosfären i vår restaurang.',
    'gallery.all': 'Alla',
    'gallery.indian': 'Indiskt',
    'gallery.thai': 'Thai',
    'gallery.sushi': 'Sushi',
    'gallery.other': 'Övrigt',
    'gallery.number': 'Nr.',
    'gallery.social': 'Följ oss på sociala medier för fler bilder och uppdateringar!',
    
    // Contact
    'contact.title': 'Kontakt',
    'contact.heading': 'Besök Oss Idag',
    'contact.description': 'Vi ser fram emot att välkomna dig till en oförglömlig matupplevelse.',
    'contact.phone': 'Telefon',
    'contact.email': 'Email',
    'contact.address': 'Adress',
    'contact.hours': 'Öppettider',
    'contact.seeBelow': 'Se nedan',
    'contact.hoursTitle': 'Våra Öppettider',
    'contact.winter': 'Vinteröppettider',
    'contact.summer': 'Sommaröppettider',
    'contact.monThu': 'Mån – Tor:',
    'contact.friSun': 'Fre – Sön:',
    'contact.kitchenNote': 'Obs! Köket stänger alltid en halvtimme innan restaurangen.',
    'contact.servicesTitle': 'Våra Tjänster',
    'contact.catering': 'Catering',
    'contact.cateringDesc': 'Skräddarsydd catering för ditt event',
    'contact.celebrate': 'Fira hos oss',
    'contact.celebrateDesc': 'Perfekt för fester och speciella tillfällen',
    'contact.accessible': 'Rullstolsanpassad',
    'contact.accessibleDesc': 'Vår ingång är rullstolstillgänglig',
    'contact.ctaTitle': 'Redo att uppleva våra smaker?',
    'contact.ctaDesc': 'Ring oss eller besök oss direkt. Vi ser fram emot att servera dig!',
    'contact.bookNow': 'Boka Bord Nu',
    
    // Booking
    'booking.title': 'Boka Bord',
    'booking.heading': 'Boka Bord',
    'booking.description': 'Reservera ditt bord hos oss och upplev en oförglömlig matupplevelse',
    'booking.firstName': 'Förnamn',
    'booking.lastName': 'Efternamn',
    'booking.phone': 'Telefonnummer',
    'booking.guests': 'Antal gäster',
    'booking.date': 'Datum',
    'booking.time': 'Tid',
    'booking.message': 'Meddelande (valfritt)',
    'booking.messagePlaceholder': 'Särskilda önskemål eller allergier...',
    'booking.submit': 'Skicka Bokningsförfrågan',
    'booking.submitting': 'Skickar...',
    'booking.booked': 'Bokad!',
    'booking.required': '* Obligatoriska fält. Vi bekräftar din bokning inom kort via telefon.',
    'booking.callUs': 'Du kan även boka direkt via telefon:',
    'booking.firstNamePlaceholder': 'Ditt förnamn',
    'booking.lastNamePlaceholder': 'Ditt efternamn',
    'booking.phonePlaceholder': '070-123 45 67',
    'booking.guestsPlaceholder': '4',
    'booking.email': 'E-postadress',
    'booking.emailPlaceholder': 'din@email.se',
    'booking.seating': 'Sittplats',
    'booking.seatingIndoor': 'Inomhus',
    'booking.seatingOutdoor': 'Uteservering',

    // Footer
    'footer.address': 'Adress',
    'footer.contact': 'Kontakt',
    'footer.hours': 'Öppettider',
    'footer.follow': 'Följ Oss',
    'footer.rights': 'Alla rättigheter reserverade.',
    'footer.tagline': 'Äkta thailändsk, indisk och sushi i en lyxig miljö sedan 2017.',
    'footer.quickLinks': 'Snabblänkar',
    'footer.contactUs': 'Kontakta Oss',
    'social.description': 'Följ oss på sociala medier för de senaste nyheterna och erbjudandena!',
    
    // Takeaway Menu
    'takeaway.title': 'Take Away Meny',
    'takeaway.subtitle': 'Upptäck våra autentiska smaker från Indien, Thailand och Japan',
    'takeaway.enlargeView': 'Förstora vy',
    'takeaway.minimizeView': 'Förminska vy',
    'takeaway.indian': 'Indiskt',
    'takeaway.thai': 'Thai',
    'takeaway.sushi': 'Sushi',
    'takeaway.barnmeny': 'Barnmeny',
    'takeaway.indiskaForratter': 'Indiska Förrätter',
    'takeaway.indiskaForratter.subtitle': 'Autentiska smaker från Indien',
    'takeaway.kockensRekommendation': 'Kockens Rekommendation',
    'takeaway.kockensRekommendation.subtitle': 'Samtliga huvudrätter serveras med ris',
    'takeaway.balti': 'Balti Rätter',
    'takeaway.balti.subtitle': 'Traditionella baltirätter',
    'takeaway.balti.note': 'Önskas färsk mango i din rätt? Tillägget är 10 kr per portion',
    'takeaway.tandoori': 'Tandoori Grillat',
    'takeaway.tandoori.subtitle': 'Marinerat och grillat i tandooriugn',
    'takeaway.tandoori.note': 'Serveras med stekta grönsaker & currysås',
    'takeaway.vegetariskt': 'Vegetariska Rätter',
    'takeaway.vegetariskt.subtitle': 'Grönsaker och linser i kryddiga såser',
    'takeaway.naan': 'Naan & Bröd',
    'takeaway.naan.subtitle': 'Färskt indiskt bröd',
    'takeaway.indiskaTillbehor': 'Indiska Tillbehör',
    'takeaway.indiskaTillbehor.subtitle': 'Komplettera din måltid',
    'takeaway.thaiForratter': 'Thailändska Förrätter',
    'takeaway.thaiForratter.subtitle': 'Smakfulla starter från Thailand',
    'takeaway.thaiForratter.note': 'Samtliga förrätter kan även serveras med ris eller nudlar mot ett tillägg på 35 kr',
    'takeaway.thaiHuvratter': 'Thailändska Huvudrätter',
    'takeaway.thaiHuvratter.subtitle': 'Autentiska smaker från Thailand',
    'takeaway.thaiTillbehor': 'Thailändska Tillbehör',
    'takeaway.thaiTillbehor.subtitle': 'Extra tillbehör och såser',
    'takeaway.sushiHosomaki': 'Sushi - Hosomaki',
    'takeaway.sushiHosomaki.subtitle': 'Klassiska tunna rullar',
    'takeaway.sushiUramaki': 'Sushi - Uramaki',
    'takeaway.sushiUramaki.subtitle': 'Inside-out rullar',
    'takeaway.sushiNigiri': 'Sushi - Nigiri',
    'takeaway.sushiNigiri.subtitle': 'Ris toppad med delikatesser',
    'takeaway.sushiTillbehor': 'Sushi Tillbehör',
    'takeaway.sushiTillbehor.subtitle': 'Extra tillbehör och såser',
    'takeaway.barnmenySection': 'Barnmeny',
    'takeaway.barnmenySection.subtitle': 'Barnvänliga favoriter',
    
    // Bar Menu
    'bar.title': 'Bar Meny',
    'bar.subtitle': 'Upptäck vårt utbud av vin, cocktails och drycker',
    'bar.enlargeView': 'Förstora vy',
    'bar.minimizeView': 'Förminska vy',
    'bar.vin': 'Vin',
    'bar.cocktails': 'Cocktails',
    'bar.ol': 'Öl',
    'bar.sprit': 'Sprit',
    'bar.husetsVin': 'Husets Vin',
    'bar.husetsVin.subtitle': 'Vårt husets urval',
    'bar.vittVin': 'Vitt Vin',
    'bar.vittVin.subtitle': 'Utvalda vita viner från Europa',
    'bar.rottVin': 'Rött Vin',
    'bar.rottVin.subtitle': 'Kraftfulla och eleganta röda viner',
    'bar.roseVin': 'Rosévin',
    'bar.roseVin.subtitle': 'Fräscht och fruktig',
    'bar.prosecco': 'Prosecco',
    'bar.prosecco.subtitle': 'Mousserande glädje',
    'bar.cocktails.subtitle': 'Handgjorda klassiska cocktails',
    'bar.ol.subtitle': 'Lokalt och internationellt',
    'bar.sprit.subtitle': 'Premium destillat',
    
    // Dine-in Menu
    'dinein.menu': 'MENY',
    'dinein.restaurant': 'RESTAURANG',
    'dinein.indian': 'INDISKA',
    'dinein.thai': 'THAI',
    'dinein.sushi': 'SUSHI',
    'dinein.mainCoursesNote': 'Samtliga huvudrätter serveras med ris garnerat med rostad lök, om inte annat anges.',
    'dinein.allergyNote': 'Vid allergier, vänligen rådfråga personalen angående innehållet i våra rätter.',
    'dinein.starters': 'FÖRRÄTTER',
    'dinein.chefsRec': 'KOCKENS REKOMMENDATION',
    'dinein.observeNote': 'Observera: Samtliga rätter innehåller koriander, vitlök och ingefära.',
    'dinein.balti': 'BALTI',
    'dinein.baltiNote': 'Dessa rätter kan tillagas med färsk mango, endast mot en extra kostnad av 10 kr per rätt.',
    'dinein.tandooriGrilled': 'TANDOORI & GRILLAT',
    'dinein.tandooriNote': 'Tandoorirätter serveras med stekta säsongensgrönsaker samt currysås.',
    'dinein.vegetarian': 'VEGETARISKT',
    'dinein.tandoor': 'TANDOOR',
    'dinein.sides': 'TILLBEHÖR',
    'dinein.thaiNote': 'Samtliga huvudrätter serveras med ris om inte annat anges.',
    'dinein.thaiStartersNote': 'Alla Förrätter kan serveras med Ris eller Nudlar mot en extra kostnad på 35kr per rätt.',
    'dinein.sushiNote': 'Obs! Alla sushirätter serveras med wasabi, ingefära och wakami.',
    'dinein.hosomaki': 'HOSOMAKI 8 BITAR',
    'dinein.uramaki': 'URAMAKI 8 BITAR',
    'dinein.nigiri': 'NIGIRI 2 BITAR',
    'dinein.kidsMenu': 'BARN & BURGER MENY',
    'dinein.kidsNote': 'För våra yngsta gäster erbjuder vi en särskild barnmeny med reducerade portionsstorlekar.',
    'dinein.kids': 'BARN',
    'dinein.burger': 'BURGER',
    'dinein.dessert': 'DESSERT',
    'dinein.mocktails': 'MOCKTAILS & DRYCKER',
    'dinein.contactInfo': 'KONTAKTINFORMATION',
    'dinein.phone': '📞',
    'dinein.email': '📧',
    'dinein.address': '📍',
    'dinein.close': 'Stäng',
    'dinein.previousPage': 'Föregående sida',
    'dinein.nextPage': 'Nästa sida',


  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.menu': 'Menus',
    'nav.gallery': 'Gallery',
    'nav.contact': 'Contact',
    'nav.bookTable': 'Book Table',
    
    // Hero
    'hero.welcome': 'Welcome to',
    'hero.tagline': 'Flavors that brighten your day',
    'hero.description': 'Experience authentic flavors from India, Thailand, and Japan in a luxurious and modern environment. Since 2017, we have delivered authentic dishes that unite culture and culinary excellence.',
    'hero.bookNow': 'Book Table Now',
    'hero.seeMenus': 'View Menus',
    
    // About
    'about.title': 'About Us',
    'about.heading': 'A Taste Experience Beyond the Ordinary',
    'about.description': 'Founded in 2017, Currylicious Yum Yum (formerly known as Curry Nam Nam), has the vision to bring authentic Indian, Thai, and Japanese flavors to our guests. Inspired by the rich culinary traditions of India, Thailand, and Japan, we are dedicated to offering a dining experience that reflects the vibrant cultures and exquisite flavors from these cuisines.',
    'about.freshIngredients': 'Fresh Ingredients',
    'about.freshDesc': 'We only use the freshest and most authentic ingredients',
    'about.passion': 'Passion for Food',
    'about.passionDesc': 'Every dish is prepared with care and love by our experienced chefs',
    'about.service': 'Personal Service',
    'about.serviceDesc': 'Your experience is our priority, every time',
    'about.quality': 'Award-winning Quality',
    'about.qualityDesc': 'Recognized for excellence in authentic Asian cuisine',
    'about.timeline.1.label': 'The Beginning',
    'about.timeline.1.title': 'A vision in Skanör',
    'about.timeline.1.desc': 'The dream of bringing Indian, Thai and Japanese flavors together under one roof — in the heart of Skanör.',
    'about.timeline.2.label': 'The Craft',
    'about.timeline.2.title': 'Three kitchens, one passion',
    'about.timeline.2.desc': 'Experienced chefs from three culinary traditions meet in the kitchen with authentic spices and the freshest ingredients.',
    'about.timeline.3.label': 'The Atmosphere',
    'about.timeline.3.title': 'A place to remember',
    'about.timeline.3.desc': 'Warm hospitality, care for every detail and an ambiance that welcomes you both indoors and outside.',
    'about.timeline.4.label': 'Today',
    'about.timeline.4.title': 'The journey continues',
    'about.timeline.4.desc': 'Every guest, every dish, every evening — a chapter in the ongoing story of Currylicious Yum Yum.',
    'about.quote': '"Our menu is a tribute to authentic ingredients and craftsmanship, where traditional recipes meet modern flavor profiles. Currylicious Yum Yum is a place where community and culture unite, and we welcome our guests to discover the rich flavors from India, Thailand, and Japan in a warm and inviting environment."',
    
    // Menu
    'menu.title': 'Our Menus',
    'menu.heading': 'Discover Our Culinary Creations',
    'menu.description': 'From classic curry from India to pad thai from Thailand and elegant sushi from Japan – we offer something for every taste.',
    'menu.takeaway': 'Takeaway Menu',
    'menu.takeawayDesc': 'Take our delicacies home. Perfect for a cozy evening.',
    'menu.seeTakeaway': 'View Takeaway',
    'menu.bar': 'Bar Menu',
    'menu.barDesc': 'Exclusive drinks and cocktails for a complete experience.',
    'menu.seeBar': 'View Bar Menu',
    'menu.alacarte': 'À La Carte',
    'menu.alacarteDesc': 'Our complete dine-in menu with all our specialties.',
    'menu.seeAlacarte': 'View À La Carte',
    'menu.allergies': 'Do you have special allergies or requests? We are happy to adapt our dishes to your needs.',
    'menu.contact': 'Contact Us for Customizations',
    
    // Video Gallery
    'video.title': 'Experience',
    'video.heading': 'See Our Restaurant',
    'video.description': 'Take a virtual tour through our restaurant and experience the atmosphere.',
    'video.atmosphere': 'Restaurant Atmosphere',
    'video.dishes': 'Our Dishes',
    'video.experience': 'Culinary Experience',
    
    // Gallery
    'gallery.title': 'Gallery',
    'gallery.heading': 'Visual Flavors',
    'gallery.description': 'A glimpse of some of our most popular dishes and the elegant atmosphere in our restaurant.',
    'gallery.all': 'All',
    'gallery.indian': 'Indian',
    'gallery.thai': 'Thai',
    'gallery.sushi': 'Sushi',
    'gallery.other': 'Other',
    'gallery.number': 'No.',
    'gallery.social': 'Follow us on social media for more pictures and updates!',
    
    // Contact
    'contact.title': 'Contact',
    'contact.heading': 'Visit Us Today',
    'contact.description': 'We look forward to welcoming you to an unforgettable dining experience.',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.address': 'Address',
    'contact.hours': 'Opening Hours',
    'contact.seeBelow': 'See below',
    'contact.hoursTitle': 'Our Opening Hours',
    'contact.winter': 'Winter Hours',
    'contact.summer': 'Summer Hours',
    'contact.monThu': 'Mon – Thu:',
    'contact.friSun': 'Fri – Sun:',
    'contact.kitchenNote': 'Note! The kitchen always closes half an hour before the restaurant.',
    'contact.servicesTitle': 'Our Services',
    'contact.catering': 'Catering',
    'contact.cateringDesc': 'Tailored catering for your event',
    'contact.celebrate': 'Celebrate with us',
    'contact.celebrateDesc': 'Perfect for parties and special occasions',
    'contact.accessible': 'Wheelchair Accessible',
    'contact.accessibleDesc': 'Our entrance is wheelchair accessible',
    'contact.ctaTitle': 'Ready to experience our flavors?',
    'contact.ctaDesc': 'Call us or visit us directly. We look forward to serving you!',
    'contact.bookNow': 'Book Table Now',
    
    // Booking
    'booking.title': 'Book Table',
    'booking.heading': 'Book Table',
    'booking.description': 'Reserve your table with us and experience an unforgettable dining experience',
    'booking.firstName': 'First Name',
    'booking.lastName': 'Last Name',
    'booking.phone': 'Phone Number',
    'booking.guests': 'Number of Guests',
    'booking.date': 'Date',
    'booking.time': 'Time',
    'booking.message': 'Message (optional)',
    'booking.messagePlaceholder': 'Special requests or allergies...',
    'booking.submit': 'Send Booking Request',
    'booking.submitting': 'Sending...',
    'booking.booked': 'Booked!',
    'booking.required': '* Required fields. We will confirm your booking shortly by phone.',
    'booking.callUs': 'You can also book directly by phone:',
    'booking.firstNamePlaceholder': 'Your first name',
    'booking.lastNamePlaceholder': 'Your last name',
    'booking.phonePlaceholder': '070-123 45 67',
    'booking.guestsPlaceholder': '4',
    'booking.email': 'Email Address',
    'booking.emailPlaceholder': 'your@email.com',
    'booking.seating': 'Seating',
    'booking.seatingIndoor': 'Indoor',
    'booking.seatingOutdoor': 'Outdoor terrace',

    // Footer
    'footer.address': 'Address',
    'footer.contact': 'Contact',
    'footer.hours': 'Opening Hours',
    'footer.follow': 'Follow Us',
    'footer.rights': 'All rights reserved.',
    'footer.tagline': 'Authentic Thai, Indian and sushi in a luxurious environment since 2017.',
    'footer.quickLinks': 'Quick Links',
    'footer.contactUs': 'Contact Us',
    'social.description': 'Follow us on social media for the latest news and offers!',
    
    // Takeaway Menu
    'takeaway.title': 'Takeaway Menu',
    'takeaway.subtitle': 'Discover our authentic flavors from India, Thailand and Japan',
    'takeaway.enlargeView': 'Enlarge view',
    'takeaway.minimizeView': 'Minimize view',
    'takeaway.indian': 'Indian',
    'takeaway.thai': 'Thai',
    'takeaway.sushi': 'Sushi',
    'takeaway.barnmeny': 'Kids Menu',
    'takeaway.indiskaForratter': 'Indian Starters',
    'takeaway.indiskaForratter.subtitle': 'Authentic flavors from India',
    'takeaway.kockensRekommendation': "Chef's Recommendation",
    'takeaway.kockensRekommendation.subtitle': 'All main courses are served with rice',
    'takeaway.balti': 'Balti Dishes',
    'takeaway.balti.subtitle': 'Traditional balti dishes',
    'takeaway.balti.note': 'Want fresh mango in your dish? Add 10 kr per serving',
    'takeaway.tandoori': 'Tandoori Grilled',
    'takeaway.tandoori.subtitle': 'Marinated and grilled in tandoori oven',
    'takeaway.tandoori.note': 'Served with sautéed vegetables & curry sauce',
    'takeaway.vegetariskt': 'Vegetarian Dishes',
    'takeaway.vegetariskt.subtitle': 'Vegetables and lentils in spicy sauces',
    'takeaway.naan': 'Naan & Bread',
    'takeaway.naan.subtitle': 'Fresh Indian bread',
    'takeaway.indiskaTillbehor': 'Indian Sides',
    'takeaway.indiskaTillbehor.subtitle': 'Complete your meal',
    'takeaway.thaiForratter': 'Thai Starters',
    'takeaway.thaiForratter.subtitle': 'Flavorful starters from Thailand',
    'takeaway.thaiForratter.note': 'All starters can also be served with rice or noodles for an additional 35 kr',
    'takeaway.thaiHuvratter': 'Thai Main Courses',
    'takeaway.thaiHuvratter.subtitle': 'Authentic flavors from Thailand',
    'takeaway.thaiTillbehor': 'Thai Sides',
    'takeaway.thaiTillbehor.subtitle': 'Extra sides and sauces',
    'takeaway.sushiHosomaki': 'Sushi - Hosomaki',
    'takeaway.sushiHosomaki.subtitle': 'Classic thin rolls',
    'takeaway.sushiUramaki': 'Sushi - Uramaki',
    'takeaway.sushiUramaki.subtitle': 'Inside-out rolls',
    'takeaway.sushiNigiri': 'Sushi - Nigiri',
    'takeaway.sushiNigiri.subtitle': 'Rice topped with delicacies',
    'takeaway.sushiTillbehor': 'Sushi Sides',
    'takeaway.sushiTillbehor.subtitle': 'Extra sides and sauces',
    'takeaway.barnmenySection': 'Kids Menu',
    'takeaway.barnmenySection.subtitle': 'Kid-friendly favorites',
    
    // Bar Menu
    'bar.title': 'Bar Menu',
    'bar.subtitle': 'Discover our selection of wines, cocktails and beverages',
    'bar.enlargeView': 'Enlarge view',
    'bar.minimizeView': 'Minimize view',
    'bar.vin': 'Wine',
    'bar.cocktails': 'Cocktails',
    'bar.ol': 'Beer',
    'bar.sprit': 'Spirits',
    'bar.husetsVin': 'House Wine',
    'bar.husetsVin.subtitle': 'Our house selection',
    'bar.vittVin': 'White Wine',
    'bar.vittVin.subtitle': 'Selected white wines from Europe',
    'bar.rottVin': 'Red Wine',
    'bar.rottVin.subtitle': 'Powerful and elegant red wines',
    'bar.roseVin': 'Rosé Wine',
    'bar.roseVin.subtitle': 'Fresh and fruity',
    'bar.prosecco': 'Prosecco',
    'bar.prosecco.subtitle': 'Sparkling joy',
    'bar.cocktails.subtitle': 'Handmade classic cocktails',
    'bar.ol.subtitle': 'Local and international',
    'bar.sprit.subtitle': 'Premium distillates',
    
    // Dine-in Menu
    'dinein.menu': 'MENU',
    'dinein.restaurant': 'RESTAURANT',
    'dinein.indian': 'INDIAN',
    'dinein.thai': 'THAI',
    'dinein.sushi': 'SUSHI',
    'dinein.mainCoursesNote': 'All main courses are served with rice garnished with roasted onions, unless otherwise stated.',
    'dinein.allergyNote': 'For allergies, please consult staff regarding the contents of our dishes.',
    'dinein.starters': 'STARTERS',
    'dinein.chefsRec': 'CHEF\'S RECOMMENDATION',
    'dinein.observeNote': 'Note: All dishes contain coriander, garlic and ginger.',
    'dinein.balti': 'BALTI',
    'dinein.baltiNote': 'These dishes can be prepared with fresh mango, for an additional cost of 10 kr per dish.',
    'dinein.tandooriGrilled': 'TANDOORI & GRILLED',
    'dinein.tandooriNote': 'Tandoori dishes are served with fried seasonal vegetables and curry sauce.',
    'dinein.vegetarian': 'VEGETARIAN',
    'dinein.tandoor': 'TANDOOR',
    'dinein.sides': 'SIDES',
    'dinein.thaiNote': 'All main courses are served with rice unless otherwise stated.',
    'dinein.thaiStartersNote': 'All Starters can be served with Rice or Noodles for an additional cost of 35kr per dish.',
    'dinein.sushiNote': 'Note! All sushi dishes are served with wasabi, ginger and wakame.',
    'dinein.hosomaki': 'HOSOMAKI 8 PIECES',
    'dinein.uramaki': 'URAMAKI 8 PIECES',
    'dinein.nigiri': 'NIGIRI 2 PIECES',
    'dinein.kidsMenu': 'KIDS & BURGER MENU',
    'dinein.kidsNote': 'For our youngest guests, we offer a special kids menu with reduced portion sizes.',
    'dinein.kids': 'KIDS',
    'dinein.burger': 'BURGER',
    'dinein.dessert': 'DESSERT',
    'dinein.mocktails': 'MOCKTAILS & BEVERAGES',
    'dinein.contactInfo': 'CONTACT INFORMATION',
    'dinein.phone': '📞',
    'dinein.email': '📧',
    'dinein.address': '📍',
    'dinein.close': 'Close',
    'dinein.previousPage': 'Previous page',
    'dinein.nextPage': 'Next page',


  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'sv';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  return useContext(LanguageContext);
};
